// @flow

import React, { useState, useRef } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import sha1 from 'sha1'
import youtubeParser from '../lib/youtubeParser'
import Appear from './Appear'
import Container from './Container'
import SectionTitle from './SectionTitle'
import Cta from './Cta'

type Props = {
  title: string,
  video: string,
  ctaTitle: string,
  password: string,
  image: any,
  send: string,
  password_placeholder: string,
  password_error: string,
}

const Video = ({
  title,
  video,
  ctaTitle,
  password,
  send,
  password_placeholder,
  password_error,
}: Props) => {
  const [hasAccess, setHasAccess] = useState(false)
  const [submiting, setSubmiting] = useState(false)
  const [formError, setFormError] = useState(false)
  const input = useRef(null)

  const videoID = youtubeParser(video)

  function handleSubmit() {
    if (input.current === null) return
    setSubmiting(true)
    const { value } = input.current
    if (sha1(value) === password) setHasAccess(true)
    else setFormError(true)
    setSubmiting(false)
  }

  return (
    <div className="flex flex-col items-stretch justify-start min-h-screen">
      <Appear className="flex-0">
        <SectionTitle title={title} />
      </Appear>
      <Appear className="flex items-center justify-center flex-1">
        <Container className="w-full pb-16 mb-24 lg:pb-20">
          <AnimatePresence initial={false} exitBeforeEnter>
            {!hasAccess && (
              <motion.div
                key="form"
                className="flex-1 mx-auto overflow-visible shadow-xl bg-yellow"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div className="h-4 mb-6 border-b-4 border-dashed border-light-gray bg-yellow" />
                <form
                  className="flex flex-col px-8 pt-6 pb-12 text-black md:pb-12 bg-yellow"
                  onSubmit={e => {
                    e.preventDefault()
                    handleSubmit()
                  }}
                >
                  <label className="flex flex-col">
                    {ctaTitle}
                    <input
                      type="password"
                      name="password"
                      className={`
                        rounded-xl mt-2 px-4 py-2 md:py-1 border focus:outline-none
                        transition-colors duration-150 ease-linear
                        ${formError ? "border-red-500" : "focus:border-black border-dark-gray"}
                      `}
                      placeholder={password_placeholder}
                      onChange={() => formError && setFormError(false)}
                      required
                      ref={input}
                    />
                    <AnimatePresence>
                      {formError && (
                      <motion.div
                        className="text-red-500"
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: "auto", opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ type: "tween", ease: "circOut" }}
                      >
                        <span className="block py-2">{password_error}</span>
                      </motion.div>
                      )}
                    </AnimatePresence>
                  </label>
                  <div className={`flex items-center justify-end pt-4 md:pt-3 ${submiting ? "pointer-events-none" : ""}`}>
                    <Cta
                      ctaText={send}
                      color={submiting ? "939393" : "0000ff"}
                      isButton
                      buttonType="submit"
                    />
                  </div>
                </form>
              </motion.div>
            )}
            {hasAccess && (
              <motion.div
                key="video"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.25 }}
              >
                <div
                  className="relative bg-black"
                  style={{
                    paddingBottom: "56.25%",
                    paddingTop: 25,
                    height: 0,
                  }}
                >
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src={`https://www.youtube.com/embed/${videoID}?autoplay=1`}
                    frameBorder="0"
                    allow="accelerometer;
                      autoplay;
                      encrypted-media;
                      gyroscope;
                      picture-in-picture"
                  />
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </Container>
      </Appear>
    </div>
  )
}

export default Video
